import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Bluelagoon } from "@bluelagoon";

const VARIANTS = {
    initial: { method: "sendPmfSurveyCampaign", titleKey: "sendCampaignTitle", descKey: "sendCampaignDescription", buttonKey: "sendButton" },
    followup: { method: "sendPmfSurveyFollowupCampaign", titleKey: "sendFollowupTitle", descKey: "sendFollowupDescription", buttonKey: "sendFollowupButton" },
};

export default function PmfSendCampaign({ onSent, variant = "initial" }) {
    const { t } = useTranslation();
    const cfg = VARIANTS[variant] || VARIANTS.initial;
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [confirming, setConfirming] = useState(false);

    const handleSend = useCallback(async () => {
        setSending(true);
        setConfirming(false);
        try {
            const res = await Bluelagoon[cfg.method]({});
            setResult(res.data?.data);
            if (onSent) onSent();
        } catch (err) {
            setResult({ error: err.message || "Failed to send campaign" });
        } finally {
            setSending(false);
        }
    }, [onSent, cfg.method]);

    return (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {t(`survey.admin.${cfg.titleKey}`)}
            </h3>
            <p className="text-sm text-slate-600 mb-6">
                {t(`survey.admin.${cfg.descKey}`)}
            </p>
            {!confirming && !sending && !result && (
                <button
                    onClick={() => setConfirming(true)}
                    className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                    {t(`survey.admin.${cfg.buttonKey}`)}
                </button>
            )}
            {confirming && (
                <div className="flex items-center gap-3">
                    <p className="text-sm text-amber-600 font-medium">
                        {t("survey.admin.confirmSend")}
                    </p>
                    <button
                        onClick={handleSend}
                        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                    >
                        {t("survey.admin.confirmYes")}
                    </button>
                    <button
                        onClick={() => setConfirming(false)}
                        className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                    >
                        {t("survey.admin.confirmNo")}
                    </button>
                </div>
            )}
            {sending && (
                <div className="flex items-center gap-3">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full" />
                    <p className="text-sm text-slate-600">{t("survey.admin.sending")}</p>
                </div>
            )}
            {result && !result.error && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-green-800 mb-2">
                        {t("survey.admin.campaignComplete")}
                    </p>
                    <div className="grid grid-cols-4 gap-4 text-center">
                        {["total", "sent", "skipped", "failed"].map((key) => (
                            <div key={key}>
                                <p className="text-2xl font-bold text-slate-900">{result[key]}</p>
                                <p className="text-xs text-slate-500">{t(`survey.admin.${key}`)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {result?.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-700">{result.error}</p>
                </div>
            )}
        </div>
    );
}
